import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';
import { CareerSiteLanguageType } from '@locale/locale-store';

import { api } from 'api';

import { Translation } from 'api/departments/requests';
import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

export const AddDepartmentModal = (props: ModalProps) => {
  const { current, t } = useStore();
  const { closeModal } = props;
  const { showSuccess } = useToaster();

  const [translations, setTranslations] = useState<Translation[]>(
    current.langs.map((lang: CareerSiteLanguageType) => ({ language: lang, title: '' }))
  );
  const [externalId, setExternalId] = useState('');

  const queryClient = useQueryClient();
  const submit = useMutation(() => api.departments.create(externalId, translations), {
    onSuccess: () => {
      queryClient.invalidateQueries(['departments']);
      current.loadInfo(); // To fetch the departments again for create/edit job
      showSuccess(t('modals.adddepartment.success'));
      closeModal();
    },
  });

  const setValue = (title: string, language: CareerSiteLanguageType) => {
    setTranslations((translation) =>
      translation.map((lang) => ({
        ...lang,
        title: lang.language === language ? title : lang.title,
      }))
    );
  };

  return (
    <Stack space={6}>
      <Text font="heading1" text={t('modals.adddepartment.header')} />
      <Stack space={4}>
        {current.langs.map((lang: CareerSiteLanguageType) => (
          <Stack key={lang} space={1}>
            <TextField
              labelIcon={FLAG_MAPPER_SMALL[lang]}
              label={t('modals.adddepartment.label', lang.toUpperCase())}
              onChange={(title) => setValue(title, lang)}
            />
            <Text text={t('modals.adddepartment.helptext')} font="regular14" color="purple80" />
          </Stack>
        ))}
        <Stack space={1}>
          <TextField label={t('modals.adddepartment.externalid.label')} onChange={setExternalId} />
          <Text
            text={t('modals.adddepartment.externalid.helptext')}
            font="regular14"
            color="purple80"
          />
        </Stack>
      </Stack>
      <Stack orientation="horizontal" space={4} justify="flex-end">
        <Button variant="secondary" onClick={closeModal} text={t('modals.adddepartment.cancel')} />
        <Button
          isDisabled={translations.some((translation) => !translation.title) || submit.isLoading}
          onClick={() => submit.mutate()}
          variant="primary-blue"
          text={t('modals.adddepartment.save')}
        />
      </Stack>
    </Stack>
  );
};
